/**
 * Distribution kinds, as one-character codes.
 *
 * A payment's kind determines two other things about it — how often the fund
 * pays, and whether the cash actually moved — so all three used to be stored
 * per row. They were not merely redundant, they disagreed: Harvest's parser
 * tested `/monthly/i` against the label, and "Semi-Monthly" matched, so 26
 * semi-monthly payments were recorded as monthly while Hamilton recorded the
 * same kind as null. Deriving both from the kind makes that disagreement
 * impossible rather than merely fixed.
 *
 * Codes, not labels, in the artefact. One character per payment, so a monthly
 * fund's history is a run of `m` that a compressor eats, and there is no
 * per-record dictionary to carry.
 *
 * Nothing here expands the dataset. Decoding leaves the codes as codes; a page
 * resolves the handful it is showing through `kindOf`. Turning 3,483 codes into
 * 3,483 label strings on load would cost memory to answer a question almost no
 * row is ever asked.
 */

export type KindCode = string;

export type Frequency = 'monthly' | 'semimonthly' | 'quarterly' | 'semiannual' | 'annual' | 'weekly' | 'biweekly' | null;

export interface KindMeta {
    /** As the issuer publishes it. */
    label: string;
    /** Implied by the kind. Null for one-off payments. */
    frequency: Frequency;
    /**
     * Did cash move? A year-end "Special (non-cash)" is reinvested rather than
     * paid: it raises cost base and is taxable, but no money arrives. Counting
     * it as income overstates yield — HFG's 2025 one is $4.29 a unit.
     */
    cash: boolean;
    /** Scheduled, as against a one-off. */
    recurring: boolean;
}

export const KINDS: Record<KindCode, KindMeta> = {
    m: { label: 'Monthly', frequency: 'monthly', cash: true, recurring: true },
    s: { label: 'Semi-Monthly', frequency: 'semimonthly', cash: true, recurring: true },
    q: { label: 'Quarterly', frequency: 'quarterly', cash: true, recurring: true },
    h: { label: 'Semi-Annual', frequency: 'semiannual', cash: true, recurring: true },
    y: { label: 'Annual', frequency: 'annual', cash: true, recurring: true },
    w: { label: 'Weekly', frequency: 'weekly', cash: true, recurring: true },
    b: { label: 'Bi-Weekly', frequency: 'biweekly', cash: true, recurring: true },

    // One-offs. `n` is the only kind where no cash moves, which is why isCash
    // no longer needs storing anywhere.
    x: { label: 'Special (cash)', frequency: null, cash: true, recurring: false },
    n: { label: 'Special (non-cash)', frequency: null, cash: false, recurring: false },
    p: { label: 'Special', frequency: null, cash: true, recurring: false },
    o: { label: 'Notional', frequency: null, cash: true, recurring: false },
};

/** Resolve one code. Unknown codes read as an unlabelled one-off rather than
    throwing: an issuer inventing a new kind should not blank a page. */
export const kindOf = (code: KindCode): KindMeta =>
    KINDS[code] ?? { label: code, frequency: null, cash: true, recurring: false };

export const labelOfKind = (code: KindCode): string => kindOf(code).label;
export const frequencyOfKind = (code: KindCode): Frequency => kindOf(code).frequency;
export const isCashKind = (code: KindCode): boolean => kindOf(code).cash;

/** Label back to code, for the encoder. Built from the table so the two cannot
    drift apart. */
export const CODE_BY_LABEL: Record<string, KindCode> = Object.fromEntries(
    Object.entries(KINDS).map(([code, meta]) => [meta.label.toLowerCase(), code]),
);

/**
 * The code for a label as an issuer wrote it.
 *
 * Footnote markers are stripped first: Harvest publishes "Notional**" and
 * "Notional***" in the cell itself, not only in the header, which made three
 * dictionary entries out of one kind.
 */
export const codeForLabel = (label: string): KindCode | null => {
    const cleaned = label.replace(/[*†‡\s]+$/g, '').trim().toLowerCase();
    return CODE_BY_LABEL[cleaned] ?? null;
};
